import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Typography,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Avatar,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams, useLocation } from "react-router-dom";
import DataStatusAlert from "../../../../AssetManagement/Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useSelector, useDispatch } from "react-redux";
import {
  setRowsPerPage,
  setSort,
  setPage,
  setTotalPackages,
  setFilterDataList,
} from "../../../../../features/monitoringPackagesSlice";
import { formatDate } from "../../../../../utils/helpers";
import { useMonitoringPackages } from "../../../../../customHooks/useMonitoringPackages";
import { normalizeFilters } from "../packageHelper";

interface Props {
  tableHeadings: { [key: string]: string };
}

// headers for the table columns with sortable option for each column
const tableheaders = [
  { id: 1, KEY: "package_name", label: "Package", sortable: true },
  {
    id: 2,
    KEY: "process_name", // Use the nested key
    label: "Process Name",
    sortable: true,
  },
  { id: 3, KEY: "start_time", label: "First Run", sortable: true },
  {
    id: 4,
    KEY: "end_time", // Use the custom logic key
    label: "Last Run",
    sortable: true,
  },
  {
    id: 5,
    KEY: "process_count",
    label: "Process Count",
    sortable: true,
  },
  { id: 6, KEY: "status", label: "Status", sortable: true },
  { id: 7, KEY: "brand", label: "Brand", sortable: true },
  { id: 8, KEY: "interface_name", label: "Interface", sortable: true },
  { id: 9, KEY: "run_method", label: "Run Method", sortable: true },
  { id: 10, KEY: "run_by", label: "Run By", sortable: true },
];

const PackageTable = ({ tableHeadings }: Props) => {
  const {
    packageList,
    selectedPackage,
    selectedMultiplePackages,
    selectedProcessName,
    selectedPackageStatus,
    selectedPackageBrand,
    selectedPackageInterface,
    selectedPackageDateRange,
    page,
    rowsPerPage,
    sort,
  } = useSelector((state: any) => state.monitoringPackages);
  const dispatch = useDispatch();

  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAppending, setIsAppending] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const headCells = Object.entries(tableHeadings);

  //-- Get Brand Name and interface id (custom hook)
  const { fetchMonitoringPackages, getBrandName } = useMonitoringPackages();

 const getCurrentFilters = () => ({
    package_name: selectedMultiplePackages?.length
      ? selectedMultiplePackages
      : undefined,
    process_id: selectedProcessName?.length ? selectedProcessName : undefined, // Changed from process_name
    status: selectedPackageStatus?.length ? selectedPackageStatus : undefined,
    brand_id: selectedPackageBrand?.length ? selectedPackageBrand : undefined,
    interface_id: selectedPackageInterface?.length
      ? selectedPackageInterface
      : undefined, // Added _id suffix for consistency
    single_package_name: selectedPackage?.length ? selectedPackage : undefined,
  });

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        // Default sorting by start_time (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      const asciiCompare = (a: string, b: string) => {
        const len = Math.min(a.length, b.length);

        for (let i = 0; i < len; i++) {
          const diff = a.charCodeAt(i) - b.charCodeAt(i);
          if (diff !== 0) return diff;
        }

        return a.length - b.length;
      };

      const extractLeadingNumber = (str: string) => {
        const match = str.match(/^\d+/);
        return match ? parseInt(match[0], 10) : null;
      };
      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;
        //  BRAND COLUMN FIX (FULL ASCII SORT)
        if (keytoSort === "brand") {
        const aStr = String(aValue).trim();
        const bStr = String(bValue).trim();

        const aNum = extractLeadingNumber(aStr);
        const bNum = extractLeadingNumber(bStr);

        let result = 0;

        // If both start with numbers → compare numeric prefix first
        if (aNum !== null && bNum !== null && aNum !== bNum) {
          result = aNum - bNum;
        } else {
          // fallback → strict ASCII lexicographic compare
          result = asciiCompare(aStr, bStr);
        }

        return sort.direction === "asc" ? result : -result;
      }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    // eslint-disable-next-line
    [sort]
  );

  // -- Function to handle pagination
  const handleChangePage = async (_: unknown, newPage: number) => {
    const totalPages = Math.ceil(packageList.length / rowsPerPage);
    const isLastOrSecondLastPage = newPage + 1 >= totalPages - 1;
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);

    if (
      hasMoreData &&
      isLastOrSecondLastPage &&
      packageList.length > 0 &&
      packageList.length % 1000 === 0
    ) {
      const backendPage = Math.ceil(packageList.length / 1000);

      setIsAppending(true);

      const from_date = selectedPackageDateRange[0].format("YYYY-MM-DD");
      const to_date = selectedPackageDateRange[1].format("YYYY-MM-DD");
      const currentFilters = normalizeFilters(getCurrentFilters());

      try {
        const newCount = await fetchMonitoringPackages(
          from_date,
          to_date,
          1000,
          backendPage,
          packageList,
          currentFilters
        );
        if (newCount < 1000) {
          setHasMoreData(false);
        }
      } catch (error) {
        console.error("Error fetching more logs:", error);
      } finally {
        setIsAppending(false);
      }
    }
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  //-- Set Sorted Data
  useEffect(() => {
    const modifiedPackageList =
      packageList?.length > 0 &&
      packageList?.map((item: any) => {
        return {
          ...item,
          brand: `${getBrandName(String(item.brand_id))} (${item.brand_id})`, // combined brand name and id
          interface: `${item.interface_name} (${item.interface_id})`, // combined interface name and id
        };
      });

    const sortedRows = getSortedData(modifiedPackageList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [packageList, getSortedData]);

  useEffect(() => {
    if (isAppending && page * rowsPerPage >= sortedData?.length && page !== 0) {
      dispatch(setPage(0));
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", "0");
      setSearchParams(updatedParams);
    }
    // eslint-disable-next-line
  }, [
    sortedData?.length,
    rowsPerPage,
    selectedPackage,
    selectedMultiplePackages,
    selectedProcessName,
    selectedPackageStatus,
    selectedPackageBrand,
    selectedPackageInterface,
  ]);

  useEffect(() => {
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  //-- Set Total Packages
  useEffect(() => {
    if (sortedData?.length > 0) {
      dispatch(setTotalPackages(sortedData?.length));
      dispatch(setFilterDataList(sortedData));
    } else {
      dispatch(setTotalPackages(0));
      dispatch(setFilterDataList([]));
    }
    // eslint-disable-next-line
  }, [sortedData?.length]);

  const routerLocation = useLocation();
  const buildDataLogsUrl = (pkgName: string) => {
  const params = new URLSearchParams(routerLocation.search);

  // override / add package_name filter
  params.set("package_name", pkgName);

  // reset pagination when navigating
  params.set("page", "0");

  return `/data_collection/data_logs?${params.toString()}`;
};

  return (
    <>
      <TableContainer className="BrandsTable">
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ padding: "20px", color: "#888", fontSize: "18px" }}
                >
                  No Data To Display
                </TableCell>
              </TableRow>
            ) : (
              <>
                {sortedData.length > 0 &&
                  sortedData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {headCells.map(([key], cellIndex) => (
                            <TableCell key={cellIndex} align="left">
                              {key === "package_name" ? (
                                <>
                                  <RouterLink
                                    to={buildDataLogsUrl(row[key])}
                                    style={{
                                      textDecoration: "underline",
                                      color: "#9C27B0",
                                    }}
                                    data-tracker="349"
                                  >
                                    <Typography
                                      color="secondary"
                                      fontSize="14px"
                                    >
                                      {row[key] ? row[key] : ""}
                                    </Typography>
                                  </RouterLink>
                                </>
                              ) : key === "process_name" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "start_time" ? (
                                <> {row[key] ? formatDate(row[key]) : ""}</>
                              ) : key === "end_time" ? (
                                <> {row[key] ? formatDate(row[key]) : ""}</>
                              ) : key === "process_count" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "brand_id" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "interface" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "run_method" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "run_by" ? (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <Avatar
                                    sx={{ width: 24, height: 24 }}
                                    alt={row[key] ? row[key].split(" ")[0] : ""}
                                    src="/static/images/avatar/1.jpg"
                                  />
                                  {row[key] ? row[key] : ""}
                                </Stack>
                              ) : key === "status" ? (
                                <>
                                  {row.status === "SUCCESS" && (
                                    <DataStatusAlert status="Success" />
                                  )}
                                  {row.status === "FAILURE" && (
                                    <DataStatusAlert status="Failure" />
                                  )}
                                  {row.status === "CANCELLED" && (
                                    <DataStatusAlert status="Cancelled" />
                                  )}
                                  {row.status === "RUNNING" && (
                                    <DataStatusAlert status="Running" />
                                  )}
                                </>
                              ) : row[key] ? (
                                row[key]
                              ) : (
                                ""
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))}
              </>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(sortedData?.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>
    </>
  );
};

export default PackageTable;
